// As páginas. Cada uma declara sua origem de dado e renderiza a partir dela.
// Página sem origem nativa é página que nós temos que sustentar — por isso
// o rótulo de origem aparece no cabeçalho: é um alarme de escopo visível.

import { repoAtual, github, litellm, state, SourceError } from "./api";


/** Repositório usado pelas páginas de código. */
import { empty, errorBox, esc, head, money, pill, skeleton, table, since } from "./ui";
import { icon } from "./icons";

type Render = (el: HTMLElement) => void | Promise<void>;

/** Envolve o carregamento: esqueleto, erro tratado, e o `next` da origem. */
async function load(el: HTMLElement, header: string, body: () => Promise<string>) {
  el.innerHTML = header + skeleton();
  try {
    el.innerHTML = header + (await body());
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
}

// ----------------------------------------------------------------- activity
//
// O que aconteceu, em ordem. Sessões e mensagens vêm de tabelas diferentes e
// são fundidas aqui — é a única tela que precisa das duas juntas.
export const activity: Render = async (el) => {
  const header = head({
    title: "Activity",
    sub: "Tudo que a frota fez e perguntou, do mais recente para trás",
  });

  await load(el, header, async () => {
    const [{ data: sessions }, { data: msgs }] = await Promise.all([
      state.sessions(),
      state.messages(),
    ]);

    type Ev = { quando: string; tipo: string; texto: string; tom: Parameters<typeof pill>[1] };
    const eventos: Ev[] = [
      ...(sessions ?? []).map((x) => ({
        quando: x.started_at,
        tipo: x.ended_at ? "sessão encerrada" : "sessão aberta",
        texto: `${x.role} · ${money(x.cost_cents)} · ${x.tokens.toLocaleString("pt-BR")} tokens`,
        tom: (x.ended_at ? "ok" : "run") as Parameters<typeof pill>[1],
      })),
      ...(msgs ?? []).map((m) => ({
        quando: m.created_at,
        tipo: m.answered_at ? "respondida" : "esperando você",
        texto: m.body,
        tom: (m.answered_at ? "idle" : "wait") as Parameters<typeof pill>[1],
      })),
    ].sort((a, b) => b.quando.localeCompare(a.quando));

    if (!eventos.length) {
      return empty("Nada aconteceu ainda", "Sessões e perguntas aparecem aqui conforme a frota trabalha.");
    }

    return table(
      ["When", "What", "Detail"],
      eventos.map(
        (e) => `
        <tr>
          <td class="mono">${esc(since(e.quando))}</td>
          <td>${pill(e.tipo, e.tom)}</td>
          <td>${esc(e.texto.length > 110 ? e.texto.slice(0, 110) + "…" : e.texto)}</td>
        </tr>`,
      ),
    );
  });
};

// ---------------------------------------------------------------- templates
//
// Um template é a forma de uma task recorrente. Vive em .github/ISSUE_TEMPLATE
// junto do repo — quem abre a issue pelo GitHub e quem despacha por aqui leem
// exatamente o mesmo arquivo.
export const templates: Render = async (el) => {
  const header = head({
    title: "Templates",
    actions: `<button class="btn btn-primary" disabled>+ Create Template</button>`,
  });

  await load(el, header, async () => {
    const repo = await repoAtual();
    const list = await github.contents(repo, ".github/ISSUE_TEMPLATE").catch(() => []);

    if (!list.length) {
      return empty(
        "Nenhum template",
        "Um template é a forma de uma task que se repete. Crie .github/ISSUE_TEMPLATE/ no repo e cada arquivo vira uma linha aqui.",
      );
    }

    return table(
      ["Name", "Source", "Size"],
      list
        .filter((e) => e.type === "file")
        .map(
          (e) => `
        <tr>
          <td>
            <a class="file-link" href="${esc(e.html_url)}" target="_blank" rel="noopener">
              ${icon("templates", "file-icon")}${esc(e.name.replace(/\.(md|ya?ml)$/, ""))}
            </a>
          </td>
          <td>${pill(esc(repo), "idle")}</td>
          <td class="num">${(e.size / 1024).toFixed(1)} KB</td>
        </tr>`,
        ),
    );
  });
};


// ---------------------------------------------------------------- conexões
//
// O ícone vem do catálogo do próprio gateway: `GET /v1/mcp/discover` publica
// `icon_url` por servidor conhecido. Para os que o catálogo não cobre, seguimos
// a MESMA convenção que ele usa (o CDN do Simple Icons pelo nome) — nenhum logo
// é baixado, embutido ou escolhido por nós. Se a marca não existir lá, o <img>
// falha e sobra a inicial, que é o que o app já faz no seletor de projeto.
function iconeMcp(nome: string, doCatalogo?: string): string {
  const inicial = esc((nome[0] ?? "?").toUpperCase());
  // Só desenha imagem quando a FONTE deu o endereço. Eu tinha derivado um slug
  // de CDN por conta própria para os que o catálogo não cobre: funcionava para
  // metade e, para a outra metade, disparava um pedido que falhava a cada
  // carga e piscava caixa de imagem quebrada. Sem endereço, a inicial basta.
  if (!doCatalogo) return `<span class="mcp-icone" data-inicial="${inicial}"></span>`;
  const src = doCatalogo;
  // Sem `loading="lazy"`: dentro da tabela o navegador nunca considerava estes
  // 18px "visíveis" e a imagem ficava eternamente pendente. E sem `onerror`
  // inline, que a política de conteúdo do Pages pode barrar — quem liga o
  // fallback é o JS, logo depois de pintar.
  return `<span class="mcp-icone" data-inicial="${inicial}">
    <img class="mcp-img" src="${esc(src)}" alt="" />
  </span>`;
}

/** Marca de imagem que não carregou: a inicial embaixo fica à mostra. */
function ligaFallbackIcones(el: HTMLElement): void {
  for (const img of el.querySelectorAll<HTMLImageElement>(".mcp-img")) {
    if (img.complete && img.naturalWidth === 0) img.remove();
    else img.addEventListener("error", () => img.remove(), { once: true });
  }
}

export const conexoes: Render = async (el) => {
  const header = head({ title: "Connections" });

  await load(el, header, async () => {
    // O catálogo é enfeite: se ele cair, a tabela continua de pé.
    const [list, catalogo] = await Promise.all([
      litellm.mcpServers(),
      litellm.mcpCatalogo().catch(() => []),
    ]);
    if (!list.length) {
      return empty(
        "Nenhum MCP conectado",
        "Adicione um servidor no LiteLLM para a frota alcançar serviços externos.",
      );
    }

    const porNome = new Map(catalogo.map((c) => [c.name.toLowerCase(), c]));

    return table(
      ["Name", "URL", "Transport"],
      list.map((m) => {
        const nome = m.alias ?? m.server_name ?? "—";
        const cat = porNome.get((m.server_name ?? "").toLowerCase());
        return `
        <tr>
          <td>
            <span class="mcp-nome">
              ${iconeMcp(nome, cat?.icon_url)}
              <span class="mono">${esc(nome)}</span>
            </span>
          </td>
          <td class="mono">${esc(m.url ?? "—")}</td>
          <td>${pill(m.transport ?? "http", "idle")}</td>
        </tr>`;
      }),
    );
  });

  ligaFallbackIcones(el);
};


// -------------------------------------------------------------------- admin
//
// Chaves, times e modelos: a multi-tenancy inteira do LiteLLM. É onde o corte
// de ferramenta por papel é feito — sem uma linha de autorização nossa.
export const admin: Render = async (el) => {
  const header = head({
    title: "Admin",
  });

  await load(el, header, async () => {
    const [times, modelos] = await Promise.all([
      litellm.teams().catch(() => []),
      litellm.models().catch(() => []),
    ]);

    const tabelaTimes = times.length
      ? table(
          ["Time", "Teto", "Modelos"],
          times.map(
            (t) => `
          <tr>
            <td class="mono">${esc(t.team_alias ?? t.team_id ?? "—")}</td>
            <td class="num">${t.max_budget != null ? `$${t.max_budget}` : "sem teto"}</td>
            <td>${(t.models ?? []).length || "todos"}</td>
          </tr>`,
          ),
        )
      : empty("Nenhum time", "Times separam frota, budget e permissão de ferramenta.");

    const tabelaModelos = modelos.length
      ? `<h3 style="font-family:var(--mono);font-size:12px;color:var(--ink-3);margin:22px 0 10px">
           Modelos roteados
         </h3>` +
        table(
          ["Modelo", "Tipo"],
          modelos.map((m) => {
            const audio = /whisper|glimmer/i.test(m.id);
            return `<tr>
              <td class="mono">${esc(m.id)}</td>
              <td>${pill(audio ? "áudio" : "texto", audio ? "idle" : "agent")}</td>
            </tr>`;
          }),
        )
      : "";

    return tabelaTimes + tabelaModelos;
  });
};
